import { BookingFormValidator } from "../events/booking-form-validator";
export interface PaymentOptions {
    formId: string;
    keyId?: string;
    contactId?: string;
    applicationId?: string;
    successUrl: string;
    failureUrl: string;
    total: string | number;
    item: string;
}

export interface StripeResponse {
    clientSecret: string;
    publicApiKey: string;
    amount: number;
}

    declare global {
    const Stripe: any;
    interface Window {
        initializeEventBookingPayment: Function;
        initializeCourseBookingPayment: Function;
        initializeMembershipApplicationPayment: Function;
        initializeECommercePayment: Function;
    }
}

export class PaymentService {
    private stripe: any;
    private elements: any;
    private options: PaymentOptions | null = null;

    public async initializeECommercePayment(
        formId: string,
        contactId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string
    ): Promise<void> {

        this.options = {
            formId: formId,
            contactId: contactId,
            successUrl: successUrl,
            failureUrl: failureUrl,
            total: total,
            item: item
        };
        this.options = { formId, keyId: contactId, successUrl, failureUrl, total, item };
        document.querySelector("#payment-form")
            ?.addEventListener("submit", this.handleSubmitECommerce.bind(this));
        await this.mountStripeElement("Shop Checkout", total, item);
        await this.checkStatus();
    }

    private async handleSubmitECommerce(e: Event): Promise<void> {
        e.preventDefault();

        if (!this.options) return;

        const { formId, keyId, successUrl, failureUrl } = this.options;
        if (!keyId || !successUrl || !failureUrl) return;

        await this.submitECommerceWithStripe(formId, keyId, successUrl, failureUrl);
    }

    private async submitECommerceWithStripe(
        formId: string,
        contactId: string,
        successUrl: string,
        failureUrl: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId, "AgreeToTermsAndConditions");
        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const ecommerceForm = document.getElementById(formId) as HTMLFormElement;

        try {
            const formData = JSON.stringify(this.getFormData(ecommerceForm));

            const response = await fetch("/umbraco/surface/ECommerceSurface/Save", {
                method: "POST",
                body: formData,
                headers: {
                    "Content-Type": "application/json"
                },
                cache: 'no-cache'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        } catch (err) {
            console.error(err);
            return;
        }

        const returnUrl =
            `/umbraco/surface/ECommerceSurface/ConfirmAndComplete?key=${contactId}&successUrl=${encodeURIComponent(successUrl)}&failureUrl=${encodeURIComponent(failureUrl)}`;

        this.setLoading(true);

        const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                return_url: `${window.location.origin}${returnUrl}`
            }
        });

        if (error.type === "card_error" || error.type === "validation_error") {
            this.showMessage(error.message);
        } else {
            this.showMessage("An unexpected error occurred.");
        }

        this.setLoading(false);
    }


    public async initializeEventBookingPayment(
        formId: string,
        eventId: string,
        eventBookingId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string
    ): Promise<void> {
        this.options = { formId, keyId: eventId, successUrl, failureUrl, total, item };
        document.querySelector("#payment-form")
            ?.addEventListener("submit", this.handleSubmitEventBooking.bind(this));
        await this.mountStripeElement("Event Booking", total, item, eventBookingId);
        await this.checkStatus();
    }

    public async initializeCourseBookingPayment(
        formId: string,
        courseId: string,
        courseBookingId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string
    ): Promise<void> {
        this.options = { formId, keyId: courseId, successUrl, failureUrl, total, item };
        document.querySelector("#payment-form")
            ?.addEventListener("submit", this.handleSubmitCourseBooking.bind(this));
        await this.mountStripeElement("Course Booking", total, item, courseBookingId);
        await this.checkStatus();
    }

    private async mountStripeElement(userJourney: string, total: string | number, item: string, bookingId: string = ""): Promise<void> {
        const isCourse = userJourney === "Course Booking";
        const bookingIdParam = bookingId
            ? (isCourse
                ? `&courseBookingId=${encodeURIComponent(bookingId)}`
                : `&eventBookingId=${encodeURIComponent(bookingId)}`)
            : "";

        const response = await fetch(
            `/umbraco/surface/StripeSurface/GetPaymentIntent?amount=${total}` +
            `&itemName=${encodeURIComponent(item)}` +
            `&userJourney=${encodeURIComponent(userJourney)}` +
            bookingIdParam,
            { method: "POST" }
        );

        const { clientSecret, publicApiKey }: StripeResponse = await response.json();

        this.stripe = Stripe(publicApiKey);
        this.elements = this.stripe.elements({ appearance: { theme: "stripe" }, clientSecret });
        this.elements.create("payment", { layout: "tabs" }).mount("#payment-element");
    }

    /**
     * Core invoice submission shared by event and course paths.
     * Callers supply explicit endpoints rather than a boolean flag.
     */
    private async submitInvoiceCore(
        formId: string,
        keyId: string,
        saveEndpoint: string | undefined,
        crmUrl: string,
        successUrl: string,
        failureUrl: string,
        poNumber: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId);

        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form),
            saveEndpoint
        );
        if (!saved) return;

        const url = new URL(crmUrl, window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        url.searchParams.set("paymentMethod", "invoice");
        url.searchParams.set("poNumber", poNumber);
        window.location.href = url.toString();
    }

    /**
     * Validates and clears the PO Number input.
     * Returns the trimmed value on success, or null if validation failed.
     */
    private validatePoNumber(): string | null {
        const poInput = document.getElementById("PoNumber") as HTMLInputElement | null;
        const poError = document.getElementById("PoNumberError") as HTMLElement | null;
        const poNumber = (poInput?.value ?? "").trim();

        if (!poNumber) {
            if (poInput) { poInput.classList.add("input-validation-error"); poInput.focus(); }
            if (poError) { poError.textContent = "Please enter a PO number."; poError.classList.remove("d-none"); }
            return null;
        }

        if (poInput) poInput.classList.remove("input-validation-error");
        if (poError) { poError.textContent = ""; poError.classList.add("d-none"); }
        return poNumber;
    }

    public async submitEventInvoiceFromButton(btn: HTMLElement): Promise<void> {
        const poNumber = this.validatePoNumber();
        if (!poNumber) return;

        await this.submitInvoiceCore(
            "EventBookingForm",
            btn.getAttribute("data-event-id") ?? "",
            undefined,
            "/umbraco/surface/EventBookingSurface/SaveEventBookingToCrm",
            btn.getAttribute("data-success-url") ?? "",
            btn.getAttribute("data-failure-url") ?? "",
            poNumber
        );
    }

    public async submitCourseInvoiceFromButton(btn: HTMLElement): Promise<void> {
        const poNumber = this.validatePoNumber();
        if (!poNumber) return;

        const courseId = btn.getAttribute("data-course-id") ?? "";

        await this.submitInvoiceCore(
            "CourseBookingForm",
            courseId,
            `/umbraco/surface/StripeSurface/SaveCourseBookingFormToSession?key=${courseId}`,
            "/umbraco/surface/CourseBookingSurface/SaveCourseBookingToCrm",
            btn.getAttribute("data-success-url") ?? "",
            btn.getAttribute("data-failure-url") ?? "",
            poNumber
        );
    }

    public async initializeMembershipApplicationPayment(
        formId: string, 
        contactId: string, 
        applicationId: string, 
        successUrl: string, 
        failureUrl: string, 
        total: string | number, 
        item: string
    ): Promise<void> {
        await this.checkStatus();

        this.options = {
            formId: formId,
            contactId: contactId,
            applicationId: applicationId,
            successUrl: successUrl,
            failureUrl: failureUrl,
            total: total,
            item: item
        };

        const form = document.querySelector("#payment-form");
        form?.addEventListener("submit", this.handleSubmitMembershipApplication.bind(this));

        const response = await fetch(
            `/umbraco/surface/StripeSurface/GetPaymentIntent?amount=${this.options.total}` +
            `&itemName=${encodeURIComponent(this.options.item)}` +
            `&applicationId=${encodeURIComponent(this.options.applicationId ?? "")}` +
            `&userJourney=${encodeURIComponent("Membership Application")}`,
            { method: "POST" }
        );
        
        const { clientSecret, publicApiKey }: StripeResponse = await response.json();

        this.stripe = Stripe(publicApiKey);

        const appearance = {
            theme: "stripe"
        };

        this.elements = this.stripe.elements({ appearance, clientSecret });

        const paymentElementOptions = {
            layout: "tabs"
        };

        const paymentElement = this.elements.create("payment", paymentElementOptions);
        paymentElement.mount("#payment-element");
    }

    private async handleSubmitEventBooking(e: Event): Promise<void> {
        e.preventDefault();
        if (!this.options?.keyId) return;
        const { formId, keyId, successUrl, failureUrl } = this.options;
        if (!successUrl || !failureUrl) return;
        await this.submitEventCardPayment(formId, keyId, successUrl, failureUrl);
    }

    private async handleSubmitCourseBooking(e: Event): Promise<void> {
        e.preventDefault();
        if (!this.options?.keyId) return;
        const { formId, keyId, successUrl, failureUrl } = this.options;
        if (!successUrl || !failureUrl) return;
        await this.submitCourseCardPayment(formId, keyId, successUrl, failureUrl);
    }

    private async submitEventCardPayment(
        formId: string,
        keyId: string,
        successUrl: string,
        failureUrl: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId);
        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form)
        );
        if (!saved) return;

        const url = new URL("/umbraco/surface/EventBookingSurface/SaveEventBookingToCrm", window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        url.searchParams.set("paymentMethod", "card");
        await this.confirmStripePayment(url.toString());
    }

    private async submitCourseCardPayment(
        formId: string,
        keyId: string,
        successUrl: string,
        failureUrl: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId);
        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form),
            `/umbraco/surface/StripeSurface/SaveCourseBookingFormToSession?key=${keyId}`
        );
        if (!saved) return;

        const url = new URL("/umbraco/surface/CourseBookingSurface/SaveCourseBookingToCrm", window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        url.searchParams.set("paymentMethod", "card");
        await this.confirmStripePayment(url.toString());
    }

    private async confirmStripePayment(returnUrl: string): Promise<void> {
        this.setLoading(true);
        const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: { return_url: returnUrl }
        });
        if (error.type === "card_error" || error.type === "validation_error") {
            this.showMessage(error.message);
        } else {
            this.showMessage("An unexpected error occurred.");
        }
        this.setLoading(false);
    }

    private async handleSubmitMembershipApplication(e: Event): Promise<void> {
        e.preventDefault();

        if (!this.options) return;

        const appForm = document.getElementById(this.options.formId) as HTMLFormElement;

        const returnUrl =
            `${window.location.href.split("/").slice(0, 3).join("/")}/umbraco/surface/MembershipApplicationSurface/ConfirmStripePayment?key=${this.options.contactId}&successUrl=${encodeURIComponent(this.options.successUrl)}&failureUrl=${encodeURIComponent(this.options.failureUrl)}`;

        // Validate form
        if (!appForm.checkValidity()) {
            appForm.reportValidity();
            return;
        } else {
            // save the form to session
            try {
                const form = document.getElementById(this.options.formId) as HTMLFormElement;
                const formData = JSON.stringify(this.getFormData(form));

                const response = await fetch("/umbraco/surface/MembershipApplicationSurface/SavePaymentStep", {
                    method: "POST",
                    body: formData,
                    headers: {
                        "Content-Type": "application/json"
                    },
                    cache: 'no-cache'
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

            } catch (err) {
                console.error(err);
                return;
            }
        }

        this.setLoading(true);

        const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                return_url: returnUrl
            }
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error.type === "card_error" || error.type === "validation_error") {
            this.showMessage(error.message);
        } else {
            this.showMessage("An unexpected error occurred.");
        }

        this.setLoading(false);
    }

    // Fetches the payment intent status after payment submission
    private async checkStatus(): Promise<void> {
        const clientSecret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret"
        );

        if (!clientSecret) {
            return;
        }

        const { paymentIntent } = await this.stripe.retrievePaymentIntent(clientSecret);

        switch (paymentIntent.status) {
            case "succeeded":
                this.showMessage("Payment succeeded!");
                break;
            case "processing":
                this.showMessage("Your payment is processing.");
                break;
            case "requires_payment_method":
                this.showMessage("Your payment was not successful, please try again.");
                break;
            default:
                this.showMessage("Something went wrong.");
                break;
        }
    }

    // ------- UI helpers -------

    private showMessage(messageText: string): void {
        const messageContainer = document.querySelector("#payment-message");

        if (messageContainer) {
            messageContainer.classList.remove("hidden");
            messageContainer.textContent = messageText;

            setTimeout(() => {
                messageContainer.classList.add("hidden");
                messageContainer.textContent = "";
            }, 4000);
        }
    }

    // Show a spinner on payment submission
    private setLoading(isLoading: boolean): void {
        const submitButton = document.querySelector("#submit") as HTMLButtonElement;
        const spinner = document.querySelector("#spinner");
        const buttonText = document.querySelector("#button-text");

        if (isLoading) {
            // Disable the button and show a spinner
            if (submitButton) submitButton.disabled = true;
            spinner?.classList.remove("d-none");
            buttonText?.classList.add("hidden");
        } else {
            if (submitButton) submitButton.disabled = false;
            spinner?.classList.add("d-none");
            buttonText?.classList.remove("hidden");
        }
    }

    private getFormData(form: HTMLFormElement): { [key: string]: string } {
        const formData = new FormData(form);
        const indexedArray: { [key: string]: string } = {};

        // Use getAll to handle ASP.NET's checkbox pattern, which renders both a
        // checkbox (value="true") and a hidden field (value="false") with the same
        // name. A plain forEach would let the hidden "false" always overwrite the
        // checkbox "true". Instead, prefer "true" if it appears among the values.
        for (const key of new Set(formData.keys())) {
            const values = formData.getAll(key).map(v => v.toString());
            indexedArray[key] = values.includes('true') ? 'true' : values[values.length - 1];
        }

        return indexedArray;
    }

    private getBookingFormData(form: HTMLFormElement): Record<string, any> {
        const fd = new FormData(form);
        const data: Record<string, any> = {};

        // 1) pull raw values (strings)
        for (const [key, value] of fd.entries()) {
            // ignore files here
            if (value instanceof File) continue;

            // If you have multi-select/checkbox groups with same name, support arrays:
            if (key in data) {
                data[key] = Array.isArray(data[key]) ? [...data[key], value] : [data[key], value];
            } else {
                data[key] = value;
            }
        }

        // 2) helpers
        const toBool = (v: any) => {
            if (typeof v === "boolean") return v;
            if (typeof v === "string") return v.toLowerCase() === "true" || v === "on" || v === "1";
            if (typeof v === "number") return v === 1;
            return false;
        };

        const toNumber = (v: any) => {
            if (typeof v === "number") return v;
            if (typeof v !== "string") return v;
            const n = Number(v);
            return Number.isFinite(n) ? n : v; // if not numeric, leave as-is
        };

        // 3) coerce known numeric fields (add/remove as needed)
        const numberFields = [
            "Total",
            "SubTotal",
            "TotalVat",
            "EarlyBirdDiscountTotal",
            "DiscountTotal",
            "TotalBookings",
            "PaymentMethod",
            "NavigationAction"
        ];

        for (const f of numberFields) {
            if (data[f] !== undefined && data[f] !== null && data[f] !== "") {
                data[f] = toNumber(data[f]);
            }
        }

        // 4) coerce known boolean fields (add/remove as needed)
        const boolFields = [
            "SameAsAbove",
            "InvoiceTermsAgreed",
            "PayByCard",
            "PayByInvoice",
            "ChargedToOrganisationAvailable",
            "ChargeToOrganisation",
            "TermsAllAgreed"
        ];

        for (const f of boolFields) {
            if (data[f] !== undefined && data[f] !== null && data[f] !== "") {
                data[f] = toBool(data[f]);
            }
        }

        return data;
    }

}

